import { useMutation } from "@tanstack/react-query";
import {
  createFileRoute,
  useNavigate,
} from "@tanstack/react-router";
import { Schema, SchemaGetter } from "effect";
import {
  Loader2,
  CheckCircle2,
  XCircle,
} from "lucide-react";
import { useEffect, useEffectEvent, useMemo } from "react";

import { useTRPC } from "@/infrastructure/trpc/react";
import { Button } from "@/shared/ui/button";
import { Card, CardContent } from "@/shared/ui/card";

const CallbackSearchSchema = Schema.Struct({
  installation_id: Schema.optional(
    Schema.Union([Schema.String, Schema.Finite]).pipe(
      Schema.decodeTo(Schema.String, {
        decode: SchemaGetter.transform((value) =>
          String(value)
        ),
        encode: SchemaGetter.transform((value) => value),
      })
    )
  ),
  setup_action: Schema.optional(Schema.String),
  state: Schema.optional(Schema.String),
});

export const Route = createFileRoute(
  "/app/github-callback"
)({
  validateSearch: Schema.toStandardSchemaV1(
    CallbackSearchSchema
  ),
  component: RouteComponent,
  head: () => ({
    meta: [
      { name: "robots", content: "noindex, nofollow" },
    ],
  }),
});

const OrganizationStateSchema = Schema.Struct({
  organizationId: Schema.optional(Schema.String),
});

type CallbackState =
  | {
      kind: "error";
      organizationId: string | null;
      message: string;
    }
  | {
      kind: "ready";
      organizationId: string;
      installationId: string;
      state: string;
    };

function parseCallback(
  search: typeof CallbackSearchSchema.Type
): CallbackState {
  let organizationId: string | null = null;
  let invalidState = false;
  if (search.state) {
    try {
      const base64 = search.state
        .replace(/-/g, "+")
        .replace(/_/g, "/");
      organizationId =
        Schema.decodeUnknownSync(OrganizationStateSchema)(
          JSON.parse(atob(base64))
        ).organizationId ?? null;
    } catch {
      invalidState = true;
      organizationId = null;
    }
  }
  if (search.setup_action === "request") {
    return {
      kind: "error",
      organizationId,
      message:
        "Installation request sent. A GitHub organization admin needs to approve the installation request before it can be used. Check GitHub notifications or contact your GitHub organization admin.",
    };
  }
  if (!search.state || !search.installation_id) {
    return {
      kind: "error",
      organizationId,
      message:
        "Missing required parameters from GitHub. Make sure you completed the installation process.",
    };
  }
  if (!organizationId) {
    return {
      kind: "error",
      organizationId,
      message: invalidState
        ? "Invalid state token format"
        : "Invalid state - missing organization",
    };
  }
  return {
    kind: "ready",
    organizationId,
    installationId: search.installation_id,
    state: search.state,
  };
}

function RouteComponent() {
  const search = Route.useSearch();
  const navigate = useNavigate();
  const trpc = useTRPC();
  const callback = useMemo(
    () => parseCallback(search),
    [search]
  );
  const organizationId = callback.organizationId;
  const handleCallback = useMutation(
    trpc.githubApp.handleCallback.mutationOptions()
  );
  const status =
    callback.kind === "error" || handleCallback.isError
      ? "error"
      : handleCallback.isSuccess
        ? "success"
        : "processing";
  const errorMessage =
    callback.kind === "error"
      ? callback.message
      : handleCallback.error
        ? [
            handleCallback.error.message,
            handleCallback.error.data?.code
              ? `code: ${handleCallback.error.data.code}`
              : null,
          ]
            .filter(Boolean)
            .join("\n") ||
          "Failed to complete GitHub App setup"
        : null;

  // Effect event for mutation - doesn't need to trigger effect re-runs
  const onProcessCallback = useEffectEvent(
    (params: {
      organizationId: string;
      installationId: string;
      state: string;
    }) => {
      handleCallback.mutate(params);
    }
  );

  useEffect(() => {
    if (callback.kind === "ready") {
      onProcessCallback({
        organizationId: callback.organizationId,
        installationId: callback.installationId,
        state: callback.state,
      });
    }
  }, [callback]);

  const handleNavigateToSettings = () => {
    if (organizationId) {
      void navigate({
        to: "/app/organization/$id/settings/github",
        params: { id: organizationId },
      });
    } else {
      void navigate({ to: "/app" });
    }
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-background p-4">
      <Card className="w-full max-w-md">
        <CardContent variant="p6">
          {status === "processing" && (
            <div className="flex flex-col items-center gap-4 text-center">
              <Loader2 className="h-12 w-12 animate-spin text-primary" />
              <div>
                <h2 className="text-lg font-semibold">
                  Setting up GitHub App
                </h2>
                <p className="mt-1 text-sm text-muted-foreground">
                  Please wait while we complete the setup...
                </p>
              </div>
            </div>
          )}

          {status === "success" && (
            <div className="flex flex-col items-center gap-4 text-center">
              <CheckCircle2 className="h-12 w-12 text-success-foreground" />
              <div>
                <h2 className="text-lg font-semibold">
                  GitHub App Connected!
                </h2>
                <p className="mt-1 text-sm text-muted-foreground">
                  The vivus GitHub App has been successfully
                  installed. You can now configure automatic
                  issue creation for your K8s clusters.
                </p>
              </div>
              <Button
                onClick={handleNavigateToSettings}
                className="mt-2"
              >
                Go to Settings
              </Button>
            </div>
          )}

          {status === "error" && (
            <div className="flex flex-col items-center gap-4 text-center">
              <XCircle className="h-12 w-12 text-destructive" />
              <div>
                <h2 className="text-lg font-semibold">
                  Setup Failed
                </h2>
                <p className="mt-1 text-sm text-muted-foreground">
                  {errorMessage ||
                    "An error occurred during setup"}
                </p>
              </div>
              <div className="mt-2 flex gap-2">
                <Button
                  variant="outline"
                  onClick={() => {
                    void navigate({ to: "/app" });
                  }}
                >
                  Go to Dashboard
                </Button>
                {organizationId && (
                  <Button
                    onClick={handleNavigateToSettings}
                  >
                    Try Again
                  </Button>
                )}
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
